import { DateTime } from 'luxon';

import { type DateTimeFieldFormat } from '@/types/schema/fields/DateTimeField';
import { type KnackCriteriaDateTimeValue } from '@/types/schema/KnackCriteria';
import {
  DEFAULT_LUXON_DATE,
  getLuxonDateFormatFromFieldFormat
} from '@/components/knack-date-picker/transformers/DateTimeFormatToLuxonFormat';
import {
  sanitizeDateString,
  sanitizeTimeNumberToString,
  sanitizeTimeString,
  sanitizeTimeStringFromHoursMinutes
} from '@/components/knack-date-picker/utils';

// This function handles the sanitization of a time string if the `time` property is either missing or in an unexpected format (e.g. number)
// This shouldn't happen often, but it can occur in Search view filters, where the default filter value saved in the builder is not always in the expected format.
export function getSanitizedTimeStringForCriteria(
  dateTimeValue: KnackCriteriaDateTimeValue,
  fieldFormat: DateTimeFieldFormat
): string {
  // If `time` is not present, we try to generate a string using the hours, minutes, and am_pm values
  if (dateTimeValue.time === undefined) {
    return sanitizeTimeStringFromHoursMinutes(
      dateTimeValue.hours,
      dateTimeValue.minutes,
      dateTimeValue.am_pm,
      fieldFormat
    );
  }

  // If `time` is a number, it represents the number of minutes since midnight (e.g. 540 for 9:00 AM)
  if (typeof dateTimeValue.time === 'number') {
    return sanitizeTimeNumberToString(dateTimeValue.time, fieldFormat);
  }

  return sanitizeTimeString(dateTimeValue.time, fieldFormat);
}

function transformCriteriaDateTimeValueToDate(
  selectedDateTimeValue: KnackCriteriaDateTimeValue,
  fieldFormat: DateTimeFieldFormat
) {
  // There are some inconsistencies on the data received by the server, we try to sanitize it
  const sanitizedDateTimeValue: KnackCriteriaDateTimeValue = {
    ...selectedDateTimeValue,
    date: sanitizeDateString(selectedDateTimeValue.date, fieldFormat),
    time: getSanitizedTimeStringForCriteria(selectedDateTimeValue, fieldFormat)
  };

  const normalizedDateFormat =
    getLuxonDateFormatFromFieldFormat(fieldFormat.date_format) || DEFAULT_LUXON_DATE;

  let normalizedTimeFormat = '';
  if (fieldFormat.time_format === 'HH:MM am') {
    normalizedTimeFormat = 'hh:mma';
  } else if (fieldFormat.time_format === 'HH MM (military)') {
    normalizedTimeFormat = 'HH:mm';
  }

  // Both date and time are provided
  if (sanitizedDateTimeValue.date !== '' && sanitizedDateTimeValue.time !== '') {
    return DateTime.fromFormat(
      `${sanitizedDateTimeValue.date} ${sanitizedDateTimeValue.time}`,
      `${normalizedDateFormat} ${normalizedTimeFormat}`
    ).toJSDate();
  }

  // Only date is provided (no time)
  if (sanitizedDateTimeValue.date !== '' && sanitizedDateTimeValue.time === '') {
    return DateTime.fromFormat(
      `${sanitizedDateTimeValue.date}`,
      `${normalizedDateFormat}`
    ).toJSDate();
  }

  // Only time is provided (no date)
  if (sanitizedDateTimeValue.date === '' && sanitizedDateTimeValue.time !== '') {
    // Use current date with the provided time
    const currentDate = DateTime.now().toFormat(normalizedDateFormat);

    return DateTime.fromFormat(
      `${currentDate} ${sanitizedDateTimeValue.time}`,
      `${normalizedDateFormat} ${normalizedTimeFormat}`
    ).toJSDate();
  }

  // Neither date nor time is provided
  return undefined;
}

const transformDateToCriteriaDateTimeValue = (
  newDateValue: Date,
  fieldFormat: DateTimeFieldFormat,
  isDateActive: boolean,
  isTimeActive: boolean
) => {
  const luxonDate = DateTime.fromJSDate(newDateValue);

  const dateFormat =
    getLuxonDateFormatFromFieldFormat(fieldFormat.date_format) || DEFAULT_LUXON_DATE;

  const timeFormat = fieldFormat.time_format === 'HH:MM am' ? 'hh:mma' : 'HH:mm';

  if (!luxonDate.isValid) {
    return {
      date: '',
      time: '',
      hours: '',
      minutes: '',
      am_pm: ''
    };
  }

  const date = isDateActive ? luxonDate.toFormat(dateFormat) : '';

  const time = isTimeActive
    ? {
        time: luxonDate.toFormat(timeFormat).toLowerCase(),
        hours: luxonDate.toFormat('H'),
        minutes: luxonDate.toFormat('m'),
        am_pm: luxonDate.toFormat('a') as 'AM' | 'PM'
      }
    : {
        time: '',
        hours: '',
        minutes: '',
        am_pm: ''
      };

  return {
    date,
    ...time
  };
};

export const dateTimeCriteriaValueTransformer = {
  toDate: transformCriteriaDateTimeValueToDate,
  fromDate: transformDateToCriteriaDateTimeValue
};
